import React, { useContext, useEffect, useState, useRef } from 'react';
import { DataContext } from '../../Provider/dataProvider';
import { fetchWithAuth } from '../../utils/authUtils';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Filler } from 'chart.js';
import { Doughnut, Line, Bar } from 'react-chartjs-2';
import '../../css/adminDashboard.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Filler);

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const StatBox = ({ title, value, color }) => (
  <div className="admin-stat-box" style={{ borderBottomColor: color }}>
    <div className="admin-stat-num">{value}</div>
    <p>{title}</p>
  </div>
);

const AdminDashboard = () => {
    const { data, accessToken } = useContext(DataContext);
    const [stats, setStats] = useState({
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        elderlyCount: 0,
        relativeCount: 0,
        adminCount: 0,
        totalHealthLogs: 0,
        highRiskAlerts: 0,
        mediumRiskAlerts: 0,
        lowRiskAlerts: 0,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [metrics, setMetrics] = useState({ usersDaily: [], healthDaily: [], alertsDaily: [] });
    const [auditLogs, setAuditLogs] = useState([]);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [didInitialLoad, setDidInitialLoad] = useState(false);
    const auditAbort = useRef(null);
    const pollRef = useRef(null);

    // Initialize default range (last 30 days) when token is ready
    useEffect(() => {
        if (!accessToken) return;
        const to = new Date();
        const toStr = to.toISOString().slice(0, 10);
        const from = new Date();
        from.setDate(from.getDate() - 30);
        const fromStr = from.toISOString().slice(0, 10);
        setDateFrom((v) => v || fromStr);
        setDateTo((v) => v || toStr);
    }, [accessToken]);

    // Initial load once when token and default range ready
    useEffect(() => {
        if (!accessToken) return;
        if (!dateFrom || !dateTo) return;
        if (!didInitialLoad) {
            loadDashboardStats();
            setDidInitialLoad(true);
        }
    }, [accessToken, dateFrom, dateTo, didInitialLoad]);

    // Focus refresh for snappy UX
    useEffect(() => {
        if (!accessToken) return;
        const onFocus = () => fetchAuditLogs();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [accessToken]);

    // Poll audit logs every 15s (lightweight), abort on unmount
    useEffect(() => {
        if (!accessToken) return;
        pollRef.current = setInterval(() => {
            fetchAuditLogs();
        }, 15000);
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
            if (auditAbort.current) auditAbort.current.abort();
        };
    }, [accessToken]);

    const handleApply = () => {
        if (!dateFrom || !dateTo) return;
        if (new Date(dateFrom) > new Date(dateTo)) {
            window.alert('Khoảng ngày không hợp lệ (Từ ngày > Đến ngày)');
            return;
        }
        loadDashboardStats();
    };

    const loadDashboardStats = async () => {
        try {
            setLoading(true);
            setError('');

            const statsQs = (dateFrom && dateTo) ? `?from=${encodeURIComponent(dateFrom)}&to=${encodeURIComponent(dateTo)}` : '';
            const response = await fetchWithAuth(`${API_BASE}/api/admin/dashboard/stats${statsQs}`);
            const payload = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(payload?.message || 'Không thể tải thống kê');
            }

            setStats(payload?.data || stats);
            // metrics (use range if provided)
            const mQs = (dateFrom && dateTo) ? `from=${encodeURIComponent(dateFrom)}&to=${encodeURIComponent(dateTo)}` : 'days=30';
            const mRes = await fetchWithAuth(`${API_BASE}/api/admin/metrics?${mQs}`);
            const mPayload = await mRes.json().catch(() => ({}));
            if (mRes.ok && mPayload?.success) {
                setMetrics({
                    usersDaily: Array.isArray(mPayload?.data?.usersDaily) ? mPayload.data.usersDaily : [],
                    healthDaily: Array.isArray(mPayload?.data?.healthDaily) ? mPayload.data.healthDaily : [],
                    alertsDaily: Array.isArray(mPayload?.data?.alertsDaily) ? mPayload.data.alertsDaily : [],
                });
            }

            // audit logs (latest 30 or filtered by range)
            await fetchAuditLogs();
        } catch (err) {
            setError(err?.message || 'Lỗi khi tải thống kê');
        } finally {
            setLoading(false);
        }
    };

    const fetchAuditLogs = async () => {
        try {
            if (auditAbort.current) auditAbort.current.abort();
            auditAbort.current = new AbortController();
            const rangeQs = (dateFrom && dateTo)
                ? `&from=${encodeURIComponent(dateFrom + ' 00:00:00')}&to=${encodeURIComponent(dateTo + ' 23:59:59')}`
                : '';
            const aRes = await fetchWithAuth(`${API_BASE}/api/admin/audit-logs?limit=30${rangeQs}`, { signal: auditAbort.current.signal });
            const aPayload = await aRes.json().catch(() => ({}));
            if (aRes.ok && aPayload?.success) {
                setAuditLogs(Array.isArray(aPayload?.data) ? aPayload.data : []);
            }
        } catch (e) {
            // ignore aborted or transient errors
        }
    };

    const roleLabels = ['Người cao tuổi', 'Người thân', 'Quản trị viên'];
    const roleData = [stats.elderlyCount, stats.relativeCount, stats.adminCount];
    const roleColors = ['#1e90ff', '#ffa502', '#2f3542'];

    // Format date labels to be more readable
    const formatDateLabel = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      return date.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' });
    };

    const growthLabels = metrics.usersDaily.map((d) => formatDateLabel(d.d));
    const growthValues = metrics.usersDaily.map((d) => Number(d.c || 0));

    const healthLabels = metrics.healthDaily.map((d) => formatDateLabel(d.d));
    const healthValues = metrics.healthDaily.map((d) => Number(d.c || 0));

    const alertLabels = metrics.alertsDaily.map((d) => formatDateLabel(d.d));
    const alertValues = metrics.alertsDaily.map((d) => Number(d.c || 0));

    const rangeLabel = (dateFrom && dateTo)
      ? `${new Date(dateFrom).toLocaleDateString('vi-VN')} – ${new Date(dateTo).toLocaleDateString('vi-VN')}`
      : '30 ngày qua';

    const commonOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } }
        }
    };

    if (loading) {
        return (
            <div className="admin-dashboard">
                <div className="admin-loading">
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    <p>Đang tải thống kê...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="admin-dashboard">
                <div className="admin-error">
                    <i className="fa-solid fa-circle-exclamation"></i>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-dashboard-container">
            <div className="admin-header">
                <h1><i className="fa-solid fa-chart-line"></i> Báo cáo thống kê hệ thống</h1>
            </div>

            {/* Bộ lọc theo khoảng ngày */}
            <div className="admin-filter-row" style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginTop: 8, marginBottom: 16 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Từ ngày</span>
                    <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>Đến ngày</span>
                    <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </label>
                <button type="button" className="tamcare-btn tamcare-btn-primary" onClick={handleApply}>
                    Áp dụng
                </button>
            </div>

            {/* Thẻ số liệu tổng hợp */}
            <div className="admin-stat-row">
                <StatBox title="Tổng thành viên" value={stats.totalUsers} color="#3498db" />
                <StatBox title="Người cao tuổi" value={stats.elderlyCount} color="#2ed573" />
                <StatBox title="Người chăm sóc" value={stats.relativeCount} color="#ffa502" />
                <StatBox title="Tỷ lệ Người cao tuổi" value={`${stats.totalUsers > 0 ? Math.round(100.0 * stats.elderlyCount / stats.totalUsers) : 0}%`} color="#ff4757" />
            </div>

            <div className="admin-charts-grid">
                <div className="admin-chart-card" style={{ gridColumn: 'span 2' }}>
                    <h3>Theo dõi hoạt động hệ thống</h3>
                    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                        {auditLogs.length === 0 ? (
                            <div className="admin-empty">Chưa có hoạt động nào</div>
                        ) : (
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                {auditLogs.map((log) => {
                                    const t = new Date(log.createdAt);
                                    const timeStr = isNaN(t.getTime()) ? '' : t.toLocaleTimeString('vi-VN', { hour12: false });
                                    const who = log.fullName || log.username || (log.accountId ? `Người dùng #${log.accountId}` : 'Hệ thống');
                                    const text = log.description || `${who} thực hiện hành động ${log.action}`;
                                    return (
                                        <li key={log.id} style={{ padding: '6px 0', borderBottom: '1px dashed #eee', fontSize: 13 }}>
                                            <span style={{ color: '#7f8c8d' }}>[{timeStr}]</span> {text}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
                <div className="admin-chart-card">
                    <h3>Cơ cấu vai trò người dùng</h3>
                    <div style={{ height: 280 }}>
                        <Doughnut
                            data={{
                                labels: roleLabels,
                                datasets: [{ data: roleData, backgroundColor: roleColors, borderWidth: 0 }]
                            }}
                            options={commonOptions}
                        />
                    </div>
                </div>
                <div className="admin-chart-card">
                    <h3>Tăng trưởng tài khoản mới</h3>
                    <div style={{ height: 280 }}>
                        <Line
                            data={{
                                labels: growthLabels,
                                datasets: [{
                                    label: 'Người dùng mới',
                                    data: growthValues,
                                    borderColor: '#2ed573',
                                    backgroundColor: 'rgba(46,213,115,0.1)',
                                    fill: true,
                                    tension: 0.3
                                }]
                            }}
                            options={commonOptions}
                        />
                    </div>
                </div>
                <div className="admin-chart-card">
                    <h3>Dữ liệu sức khỏe ({rangeLabel})</h3>
                    <div style={{ height: 280 }}>
                        <Line
                            data={{
                                labels: healthLabels,
                                datasets: [{
                                    label: 'Bản ghi sức khỏe',
                                    data: healthValues,
                                    borderColor: '#3498db',
                                    backgroundColor: 'rgba(52,152,219,0.1)',
                                    fill: true,
                                    tension: 0.3
                                }]
                            }}
                            options={commonOptions}
                        />
                    </div>
                </div>
                <div className="admin-chart-card">
                    <h3>Cảnh báo AI ({rangeLabel})</h3>
                    <div style={{ height: 280 }}>
                        <Line
                            data={{
                                labels: alertLabels,
                                datasets: [{
                                    label: 'Số cảnh báo',
                                    data: alertValues,
                                    borderColor: '#ff4757',
                                    backgroundColor: 'rgba(255,71,87,0.1)',
                                    fill: true,
                                    tension: 0.3
                                }]
                            }}
                            options={commonOptions}
                        />
                    </div>
                </div>
                <div className="admin-chart-card" style={{ gridColumn: 'span 2' }}>
                    <h3>Thống kê tăng trưởng tài khoản ({rangeLabel})</h3>
                    <div style={{ height: 280 }}>
                        <Bar
                            data={{
                                labels: growthLabels,
                                datasets: [{
                                    label: 'Số tài khoản mới',
                                    data: growthValues,
                                    backgroundColor: '#3498db',
                                    borderRadius: 6
                                }]
                            }}
                            options={{
                                ...commonOptions,
                                scales: {
                                    y: { beginAtZero: true, ticks: { stepSize: 1 } }
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
